import { Divider, Input, SimpleGrid, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import DatePickerInput from "src/components/shared/_FormInputs/DatePickerInput";
import Switch from "src/components/shared/_FormInputs/Switch";
import { Button } from "src/components/shared/Button/Button";
import { useCheckPerm, useIsOnline } from "src/hooks";
import { biofoulingSetupFields } from "src/pages/Settings/YachtSetup/BiofoulingSetUp/BiofoulingSetUp.utils";
import {
  getNextFieldLabel,
  getNextFieldName,
  gmdssSetupFields,
} from "src/pages/Settings/YachtSetup/GmdssSetup/GmdssSetup.utils";
import styles from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup.module.scss";
import {
  FieldsSection,
  VesselParticularsCard,
  VesselParticularsContainer,
  VesselParticularsGrid,
  VesselSectionTitle,
} from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup.styled";
import { IomData } from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup.types";
import { vesselParticularsFields } from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup.utils";
import { type TYachtSetupField } from "src/pages/Settings/YachtSetup/yachtSetup.types";

import IomInputs from "./VesselParticulars/IomInputs/IomInputs";
import VesselTypeInput from "./VesselParticulars/VesselTypeInput";

const dataFields = {
  vessel: vesselParticularsFields,
  gmdss: gmdssSetupFields,
  biofoul: biofoulingSetupFields,
};

interface Props {
  type: keyof typeof dataFields;
  dynamicFields?: Record<string, any>;
  iomData?: Record<string, IomData>;
}

const YachtDetailsForm = ({ type, dynamicFields, iomData }: Props) => {
  const { register, control, reset, getValues } = useFormContext();
  const isOnline = useIsOnline();
  const checkPerm = useCheckPerm();
  const readOnly = !checkPerm("editparticulars") || !isOnline;
  const [fields, setFields] = useState(dataFields);
  const isIomVessel = Boolean(iomData && Object.keys(iomData).length > 0);

  useEffect(() => {
    if (dynamicFields) {
      const copiedFields = JSON.parse(JSON.stringify(fields));
      Object.keys(dynamicFields).forEach(key => {
        const isArrayPath = /^[a-z]+\.\d+\..+$/.test(key);
        const timesToAdd = isArrayPath
          ? dynamicFields[key].length - 1
          : dynamicFields[key].length / 2 - 1;
        Array.from({ length: timesToAdd }, () => {
          handleAddField(key, copiedFields);
        });
      });
      setFields(copiedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, []);

  const handleAddField = (section: string, copiedFields?: any) => {
    const data = copiedFields ?? fields;
    const arrayMatch = section.match(/^([a-z]+)\.\d+\.(.+)$/);
    const lastField = arrayMatch
      ? data[type].findLast((el: TYachtSetupField) =>
          new RegExp(`^${arrayMatch[1]}\\.\\d+\\.${arrayMatch[2]}$`).test(
            el.name ?? "",
          ),
        )
      : data[type].findLast((el: TYachtSetupField) =>
          el.name?.includes(section),
        );
    const indexOfLastField = data[type].indexOf(lastField);
    const preLastField = data[type][indexOfLastField - 1];

    const newFields = [
      {
        name: getNextFieldName(preLastField.name ?? ""),
        label: getNextFieldLabel(preLastField),
      },
      {
        name: getNextFieldName(lastField.name ?? ""),
        label: getNextFieldLabel(lastField),
      },
    ];
    if (!copiedFields) {
      setFields(prevFields => ({
        ...prevFields,
        [type]: [
          ...prevFields[type].slice(0, indexOfLastField + 1),
          ...newFields,
          ...prevFields[type].slice(indexOfLastField + 1),
        ],
      }));
      if (arrayMatch) {
        const arrName = arrayMatch[1];
        const currentValues = getValues();
        const newArr = [
          ...((currentValues as any)[arrName] ?? []),
          { serial: "", exp: "" },
        ];
        reset({ ...currentValues, [arrName]: newArr });
      } else {
        reset({
          ...getValues(),
          [newFields[0].name]: "",
          [newFields[1].name]: "",
        });
      }
    } else {
      copiedFields[type] = [
        ...copiedFields[type].slice(0, indexOfLastField + 1),
        ...newFields,
        ...copiedFields[type].slice(indexOfLastField + 1),
      ];
      if (!arrayMatch) {
        reset({
          ...getValues(),
          [newFields[0].name]: "",
          [newFields[1].name]: "",
        });
      }
    }
  };

  const renderFieldRow = (field: TYachtSetupField) => (
    <Input.Wrapper key={field.name ?? field.label} label={field.label}>
      {field.type === "date" ? (
        <DatePickerInput control={control} name={field.name ?? ""} />
      ) : (
        <Input {...register(field.name ?? "")} readOnly={readOnly} />
      )}
    </Input.Wrapper>
  );

  const renderDefaultFields = () =>
    fields[type].map(field => {
      if (field?.line) {
        return (
          <Divider
            key={field.label}
            mt="sm"
            label={field.label}
            labelPosition="center"
            styles={{ root: { width: "90%" } }}
          />
        );
      }
      if (dynamicFields && "button" in field && field?.button) {
        return (
          <Button
            key={field.label}
            onClick={() => handleAddField(field.type ?? "")}>
            {field.label}
          </Button>
        );
      }
      return renderFieldRow(field);
    });

  if (type === "vessel") {
    return (
      <VesselParticularsContainer>
        <VesselParticularsGrid
          twoColumn={isIomVessel}
          singleSection={!isIomVessel}>
          <VesselParticularsCard
            className={
              !isIomVessel ? styles.vesselParticularsCardFullWidth : undefined
            }>
            <VesselSectionTitle>
              <Text component="span" className={styles.sectionTitle}>
                Details
              </Text>
            </VesselSectionTitle>
            <FieldsSection>
              <SimpleGrid
                cols={
                  isIomVessel
                    ? { base: 1, sm: 2 }
                    : { base: 1, sm: 2, lg: 3, xl: 4 }
                }
                spacing="sm">
                {fields[type].map(field => renderFieldRow(field))}
                <Input.Wrapper label="Logbook Working Language">
                  <Input {...register("lblang")} readOnly={readOnly} />
                </Input.Wrapper>
                <VesselTypeInput />
                <Input.Wrapper label="Transform all entries to uppercase">
                  <Switch
                    name="uppercase"
                    control={control}
                    disabled={readOnly}
                  />
                </Input.Wrapper>
              </SimpleGrid>
            </FieldsSection>
          </VesselParticularsCard>

          {isIomVessel && iomData && (
            <VesselParticularsCard>
              <VesselSectionTitle>
                <Text component="span" className={styles.sectionTitle}>
                  IOM
                </Text>
              </VesselSectionTitle>
              <FieldsSection className={styles.iomFieldsSection}>
                <IomInputs iomData={iomData} />
              </FieldsSection>
            </VesselParticularsCard>
          )}
        </VesselParticularsGrid>
      </VesselParticularsContainer>
    );
  }

  return (
    <SimpleGrid
      cols={{ base: 1, sm: 2 }}
      spacing="sm"
      maw="600px"
      w="100%"
      mx="auto">
      {renderDefaultFields()}
    </SimpleGrid>
  );
};

export default YachtDetailsForm;
